// Activity feed — /<slug>/activity?name=<uploader>
// Shows comments on photos uploaded by the current localStorage display_name.
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Bell, BellOff, ArrowLeft, MessageCircle, Image as ImageIcon, Play } from "lucide-react";
import { toast } from "sonner";
import PageShell from "../components/PageShell";
import { Albums, Activity, Media } from "../lib/api";
import { pushSupported, getNotificationPermission, subscribePush, unsubscribePush, isCurrentlySubscribed } from "../lib/push";
import { getGuestId } from "../lib/guestId";

const NAME_KEY = "meyecam.display_name";
const SEEN_KEY = (slug, ident) => `meyecam.activity_seen.${slug}.${ident}`;

export default function ActivityPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [draftName, setDraftName] = useState("");

  useEffect(() => {
    const n = (localStorage.getItem(NAME_KEY) || "").trim();
    setName(n);
    setDraftName(n);
    Albums.get(slug).then(setAlbum).catch(() => navigate(`/${slug}`));
  }, [slug, navigate]);

  useEffect(() => {
    if (!name) { setItems([]); setLoading(false); return; }
    setLoading(true);
    const gid = getGuestId();
    Activity.for(slug, name, undefined, gid)
      .then((d) => setItems(d.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // Mark "seen up to now"
    const ident = gid || name.toLowerCase();
    localStorage.setItem(SEEN_KEY(slug, ident), new Date().toISOString());
  }, [slug, name]);

  if (!album) {
    return <PageShell><div className="flex justify-center mt-24"><div className="meye-spinner" /></div></PageShell>;
  }

  return (
    <PageShell
      headerLeft={
        <button onClick={() => navigate(`/${slug}`)} className="meye-chip" style={{ padding: "8px 12px" }} data-testid="back-from-activity">
          <ArrowLeft size={14} /> Back
        </button>
      }
    >
      <div className="flex items-center gap-3 mt-1 mb-5">
        <div
          className="flex items-center justify-center"
          style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(127,75,255,0.18)", border: "1px solid rgba(127,75,255,0.5)" }}
        >
          <Bell size={22} color="#7F4BFF" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xl font-semibold tracking-tight">Your notifications</div>
          <div className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
            Comments on photos you uploaded as <span style={{ color: "#22E2FF" }}>{name || "—"}</span>
          </div>
        </div>
      </div>

      {!name ? (
        <div className="glass-card p-5">
          <div className="font-semibold mb-2">What's your name?</div>
          <div className="text-sm mb-3" style={{ color: "rgba(255,255,255,0.6)" }}>
            We use the name you uploaded with to find comments on your photos.
          </div>
          <input
            className="meye-input mb-3"
            placeholder="e.g. Rick"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            maxLength={50}
            data-testid="activity-name-input"
          />
          <button
            className="cta-pill w-full"
            disabled={!draftName.trim()}
            onClick={() => {
              const n = draftName.trim();
              localStorage.setItem(NAME_KEY, n);
              setName(n);
            }}
            data-testid="activity-set-name-btn"
          >
            Show my notifications
          </button>
        </div>
      ) : (
        <>
          <NotifyToggle slug={slug} name={name} />
          {loading ? (
        <div className="flex justify-center mt-10"><div className="meye-spinner" /></div>
      ) : items.length === 0 ? (
        <div className="glass-card p-8 text-center" style={{ color: "rgba(255,255,255,0.6)" }} data-testid="empty-activity">
          <MessageCircle size={28} style={{ margin: "0 auto", opacity: 0.5 }} />
          <div className="mt-3">No comments on your photos yet.</div>
          <Link to={`/${slug}/upload`} className="font-bold mt-2 inline-block text-sm" style={{ color: "#22E2FF" }}>
            Upload more →
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5" data-testid="activity-list">
          {items.map(({ comment, media }) => (
            <Link
              key={comment.id}
              to={`/${slug}/media/${media.id}`}
              className="glass-card p-3 flex items-center gap-3"
              data-testid={`activity-item-${comment.id}`}
            >
              <div
                className="flex-shrink-0 rounded-lg overflow-hidden"
                style={{ width: 56, height: 56, background: "rgba(255,255,255,0.05)" }}
              >
                {!media.is_video ? (
                  <img
                    src={Media.thumbUrl(media.id)}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Play size={20} color="#22E2FF" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  <span className="font-semibold">{comment.author_name}</span>
                  <span style={{ color: "rgba(255,255,255,0.55)" }}> commented on </span>
                  <span style={{ color: "rgba(255,255,255,0.85)" }}>
                    {media.title || "your photo"}
                  </span>
                </div>
                <div className="text-sm truncate" style={{ color: "rgba(255,255,255,0.72)" }}>
                  "{comment.text}"
                </div>
                <div className="text-[11px]" style={{ color: "rgba(255,255,255,0.4)" }}>
                  {new Date(comment.created_at).toLocaleString()}
                </div>
              </div>
              <div className="text-xs flex-shrink-0" style={{ color: "#22E2FF" }}>
                Reply →
              </div>
            </Link>
          ))}
        </div>
      )}
        </>
      )}
    </PageShell>
  );
}

// Small toggle card to enable/disable browser push for this uploader.
const NotifyToggle = ({ slug, name }) => {
  const [supported] = useState(() => pushSupported());
  const [subscribed, setSubscribed] = useState(false);
  const [perm, setPerm] = useState("default");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setPerm(await getNotificationPermission());
      setSubscribed(isCurrentlySubscribed());
    })();
  }, []);

  if (!supported) {
    return (
      <div className="glass-card p-3 mb-4 text-xs" style={{ color: "rgba(255,255,255,0.55)" }} data-testid="push-unsupported">
        Browser push notifications aren't supported here. We'll still show the bell when you open mEYEcam.
      </div>
    );
  }

  const onToggle = async () => {
    setBusy(true);
    try {
      if (subscribed) {
        await unsubscribePush();
        setSubscribed(false);
        toast.success("Notifications off");
      } else {
        await subscribePush({ albumSlug: slug, uploaderName: name });
        setSubscribed(true);
        setPerm(await getNotificationPermission());
        toast.success("You'll get a ping when someone comments on your photos");
      }
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
      setPerm(await getNotificationPermission());
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass-card p-3 mb-4 flex items-center gap-3" data-testid="notify-toggle">
      <div
        className="flex items-center justify-center flex-shrink-0"
        style={{
          width: 36, height: 36, borderRadius: 10,
          background: subscribed ? "rgba(34,226,255,0.15)" : "rgba(255,255,255,0.06)",
          border: `1px solid ${subscribed ? "rgba(34,226,255,0.45)" : "rgba(255,255,255,0.10)"}`,
        }}
      >
        {subscribed ? <Bell size={16} color="#22E2FF" /> : <BellOff size={16} color="rgba(255,255,255,0.6)" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">
          {subscribed ? "Push notifications on" : "Get notified of new comments"}
        </div>
        <div className="text-xs" style={{ color: "rgba(255,255,255,0.55)" }}>
          {perm === "denied"
            ? "Permission blocked in browser settings."
            : subscribed
              ? "We'll ping you even when this tab is closed."
              : "Allow your browser to send you a heads-up."}
        </div>
      </div>
      <button
        onClick={onToggle}
        disabled={busy || perm === "denied"}
        className="meye-chip"
        style={{
          padding: "8px 14px",
          background: subscribed ? "rgba(255,255,255,0.04)" : "rgba(34,226,255,0.10)",
          borderColor: subscribed ? "rgba(255,255,255,0.10)" : "rgba(34,226,255,0.4)",
          color: subscribed ? "rgba(255,255,255,0.75)" : "#22E2FF",
          fontWeight: 600,
        }}
        data-testid="notify-toggle-btn"
      >
        {busy ? <div className="meye-spinner" /> : subscribed ? "Turn off" : "Turn on"}
      </button>
    </div>
  );
};
